/**
 * Alertas y notificaciones en el idioma de avisos y soporte.
 *
 * Lo ideal es que el texto ya venga con `$tn()`, pero muchos avisos se arman antes
 * de llegar a `Swal.fire` (en variables o en helpers que reciben `this.$t`) y salen
 * en el idioma de la app. Acá, al mostrarse, cada texto que coincide con una
 * traducción del idioma de la app (también con variables, «{model}») se cambia por
 * la misma clave en el idioma de avisos. Lo que no coincide queda como vino.
 */
import Swal from "sweetalert2";
import { i18n, getNoticeLocale } from "@/i18n";

const SWAL_TEXT_OPTIONS = [
  "title",
  "text",
  "confirmButtonText",
  "cancelButtonText",
  "denyButtonText",
  "inputPlaceholder",
  "inputLabel",
  "validationMessage",
  "footer",
];
const SWAL_HTML_OPTIONS = ["html", "footer"];

let indexCache = { locale: "", messages: null, exact: null, templates: null };

function flatten(obj, prefix, out) {
  for (const [k, v] of Object.entries(obj || {})) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) flatten(v, path, out);
    else if (typeof v === "string" && v.trim()) out.push([path, v]);
  }
  return out;
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Índice valor → clave del idioma de la app. Se rehace si cambian el idioma o los mensajes. */
function appIndex(appLocale) {
  const messages = i18n.messages && i18n.messages[appLocale];
  if (indexCache.locale === appLocale && indexCache.messages === messages) return indexCache;
  const exact = new Map();
  const templates = [];
  for (const [key, value] of flatten(messages, "", [])) {
    if (value.includes(" | ")) continue; // plurales
    const trimmed = value.trim();
    if (/\{\w+\}/.test(trimmed)) {
      const names = [];
      const source = escapeRegExp(trimmed).replace(/\\\{(\w+)\\\}/g, (_, name) => {
        names.push(name);
        return "([\\s\\S]+?)";
      });
      templates.push({ key, names, re: new RegExp(`^${source}$`) });
    } else if (!exact.has(trimmed)) {
      exact.set(trimmed, key);
    }
  }
  indexCache = { locale: appLocale, messages, exact, templates };
  return indexCache;
}

function translateString(value, appLocale, noticeLocale) {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  const { exact, templates } = appIndex(appLocale);
  const lead = value.slice(0, value.indexOf(trimmed));
  const tail = value.slice(lead.length + trimmed.length);
  const key = exact.get(trimmed);
  if (key && i18n.te(key, noticeLocale)) {
    return lead + i18n.t(key, noticeLocale) + tail;
  }
  for (const tpl of templates) {
    const m = tpl.re.exec(trimmed);
    if (!m || !i18n.te(tpl.key, noticeLocale)) continue;
    const params = {};
    tpl.names.forEach((name, i) => {
      params[name] = m[i + 1];
    });
    return lead + i18n.t(tpl.key, noticeLocale, params) + tail;
  }
  return value;
}

function translateHtml(html, appLocale, noticeLocale) {
  if (typeof html !== "string" || !html.trim()) return html;
  if (!/[<&]/.test(html)) return translateString(html, appLocale, noticeLocale);
  try {
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_TEXT);
    let changed = false;
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const next = translateString(node.nodeValue, appLocale, noticeLocale);
      if (next !== node.nodeValue) {
        node.nodeValue = next;
        changed = true;
      }
    }
    return changed ? tpl.innerHTML : html;
  } catch (e) {
    return html;
  }
}

function locales() {
  const appLocale = i18n.locale;
  const noticeLocale = getNoticeLocale();
  return appLocale && noticeLocale && appLocale !== noticeLocale ? { appLocale, noticeLocale } : null;
}

/** Devuelve el texto en el idioma de avisos si es una traducción conocida de la app. */
export function toNoticeLanguage(value) {
  const loc = locales();
  return loc ? translateString(value, loc.appLocale, loc.noticeLocale) : value;
}

function translateSwalOptions(options) {
  const loc = locales();
  if (!loc || !options || typeof options !== "object") return options;
  const out = { ...options };
  for (const name of SWAL_TEXT_OPTIONS) {
    if (typeof out[name] === "string" && !SWAL_HTML_OPTIONS.includes(name)) {
      out[name] = translateString(out[name], loc.appLocale, loc.noticeLocale);
    }
  }
  for (const name of SWAL_HTML_OPTIONS) {
    if (typeof out[name] === "string") out[name] = translateHtml(out[name], loc.appLocale, loc.noticeLocale);
  }
  return out;
}

function translateSwalArgs(args) {
  if (args.length && args[0] && typeof args[0] === "object" && !(args[0] instanceof Node)) {
    return [translateSwalOptions(args[0]), ...args.slice(1)];
  }
  // Swal.fire(title, html, icon)
  const loc = locales();
  if (!loc) return args;
  return args.map((a, i) =>
    i === 0 ? translateString(a, loc.appLocale, loc.noticeLocale)
      : i === 1 ? translateHtml(a, loc.appLocale, loc.noticeLocale)
        : a
  );
}

function patchSwalClass(SwalClass) {
  if (!SwalClass || SwalClass.__noticeLanguagePatched) return SwalClass;
  const originalFire = SwalClass.fire;
  SwalClass.fire = function fire(...args) {
    return originalFire.apply(this, translateSwalArgs(args));
  };
  const originalMixin = SwalClass.mixin;
  if (originalMixin) {
    SwalClass.mixin = function mixin(params) {
      return patchSwalClass(originalMixin.call(this, translateSwalOptions(params)));
    };
  }
  const originalValidation = SwalClass.showValidationMessage;
  if (originalValidation) {
    SwalClass.showValidationMessage = function showValidationMessage(message) {
      return originalValidation.call(this, toNoticeLanguage(message));
    };
  }
  SwalClass.__noticeLanguagePatched = true;
  return SwalClass;
}

/** Notificación del plugin ($notify): `message` y `title`. */
export function translateNotification(notification) {
  if (!notification || typeof notification !== "object") return notification;
  const loc = locales();
  if (!loc) return notification;
  const out = { ...notification };
  if (typeof out.message === "string") out.message = translateHtml(out.message, loc.appLocale, loc.noticeLocale);
  if (typeof out.title === "string") out.title = translateString(out.title, loc.appLocale, loc.noticeLocale);
  return out;
}

export function installNoticeLanguage() {
  patchSwalClass(Swal);
}
